import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search as SearchIcon, UserPlus, UserCheck, Share2, Users, ChevronDown } from "lucide-react";
import { api, streamUrl, picUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { BadgeWithCelebration } from "@/components/CreatorBadge";

function formatCount(n) {
  if (n == null) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export default function Search() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ users: [], videos: [] });
  const [trending, setTrending] = useState([]);
  const [topCreators, setTopCreators] = useState([]);
  const [topSharers, setTopSharers] = useState([]);
  const [topReferrers, setTopReferrers] = useState([]);
  const [followBusy, setFollowBusy] = useState({});
  // Per-section collapse state — each section shows first 10 rows and
  // "See more" expands the rest.
  const [expanded, setExpanded] = useState({
    creators: false,
    sharers: false,
    referrers: false,
  });
  // Weekly-vs-all-time range for the sharer + referrer leaderboards so
  // newer users have a fair shot at cracking the top 3.
  const [sharerRange, setSharerRange] = useState("all");
  const [referrerRange, setReferrerRange] = useState("all");

  useEffect(() => {
    api
      .get("/discover/trending")
      .then((r) => {
        const data = r.data;
        // Endpoint returns { period, tags } — support legacy array too.
        setTrending(Array.isArray(data) ? data : data?.tags || []);
      })
      .catch(() => {});
    api
      .get("/discover/top-creators", { params: { limit: 50 } })
      .then((r) => setTopCreators(r.data?.creators || []))
      .catch(() => setTopCreators([]));
    api
      .get("/discover/top-video-sharers", { params: { limit: 50, range: sharerRange } })
      .then((r) => setTopSharers(r.data?.items || []))
      .catch(() => setTopSharers([]));
    api
      .get("/discover/top-referrers", { params: { limit: 50, range: referrerRange } })
      .then((r) => setTopReferrers(r.data?.items || []))
      .catch(() => setTopReferrers([]));
  }, [sharerRange, referrerRange]);

  useEffect(() => {
    if (!q.trim()) {
      setResults({ users: [], videos: [] });
      return;
    }
    const t = setTimeout(() => {
      api
        .get("/search", { params: { q } })
        .then((r) => setResults(r.data))
        .catch(() => setResults({ users: [], videos: [] }));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const toggleFollow = async (creator) => {
    if (!user) {
      toast.error("Sign in to follow creators");
      return;
    }
    if (followBusy[creator.user_id]) return;
    setFollowBusy((b) => ({ ...b, [creator.user_id]: true }));
    const wasFollowing = creator.is_following;
    // optimistic
    setTopCreators((prev) =>
      prev.map((c) =>
        c.user_id === creator.user_id
          ? {
              ...c,
              is_following: !wasFollowing,
              follower_count: Math.max(
                0,
                (c.follower_count || 0) + (wasFollowing ? -1 : 1)
              ),
            }
          : c
      )
    );
    try {
      if (wasFollowing) {
        await api.delete(`/users/${creator.username}/follow`);
      } else {
        await api.post(`/users/${creator.username}/follow`);
      }
    } catch (e) {
      // revert
      setTopCreators((prev) =>
        prev.map((c) =>
          c.user_id === creator.user_id
            ? {
                ...c,
                is_following: wasFollowing,
                follower_count: Math.max(
                  0,
                  (c.follower_count || 0) + (wasFollowing ? 1 : -1)
                ),
              }
            : c
        )
      );
      toast.error("Could not update follow");
    } finally {
      setFollowBusy((b) => ({ ...b, [creator.user_id]: false }));
    }
  };

  return (
    <div className="md:ml-60 min-h-screen pb-24 md:pb-12">
      <div className="max-w-3xl mx-auto px-6 pt-10">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF007F] mb-2">
          // discover.now
        </p>
        <h1 className="font-display font-black text-4xl mb-8">Search</h1>

        <div className="relative mb-10">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="users · captions · #hashtags"
            className="w-full bg-white/5 border border-white/10 pl-12 pr-4 py-4 text-white placeholder:text-white/30 focus:outline-none focus:border-[#00FFFF] focus:ring-1 focus:ring-[#00FFFF]/40 font-mono text-sm"
          />
        </div>

        {!q && topCreators.length > 0 && (
          <div className="mb-10" data-testid="top-creators-section">
            <div className="flex items-center justify-between mb-4">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40">
                // top.creators
              </p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/30">
                {topCreators.length} total
              </p>
            </div>
            <div className="space-y-2">
              {(expanded.creators ? topCreators : topCreators.slice(0, 10)).map((c, idx) => (
                <div
                  key={c.user_id}
                  data-testid={`top-creator-${c.username}`}
                  className="group flex items-center gap-4 p-3 border border-white/10 hover:border-[#FF007F]/60 hover:bg-[#FF007F]/5 transition-all"
                >
                  <span className="font-mono text-xs text-[#00FFFF] w-6 tabular-nums">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <Link
                    to={user?.is_admin ? `/admin/user/${c.username}/shares` : `/u/${c.username}`}
                    data-testid={`top-creator-link-${c.username}`}
                    className="flex items-center gap-4 flex-1 min-w-0"
                  >
                    {c.picture ? (
                      <img
                        src={picUrl(c.picture)}
                        alt=""
                        className="h-12 w-12 object-cover border border-white/10 group-hover:border-[#FF007F]/60 transition-all"
                      />
                    ) : (
                      <div className="h-12 w-12 bg-[#FF007F]/20 flex items-center justify-center font-bold text-[#FF007F] border border-white/10">
                        {c.name?.[0]?.toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold truncate flex items-center gap-1.5">
                        {c.name}
                        {user?.is_admin && (
                          <span className="font-mono text-[8px] uppercase tracking-widest text-[#FF007F] border border-[#FF007F]/50 px-1 py-0.5">
                            admin
                          </span>
                        )}
                      </p>
                      <p className="font-mono text-xs text-white/40 truncate">
                        @{c.username} · {formatCount(c.follower_count)}{" "}
                        {c.follower_count === 1 ? "follower" : "followers"}
                      </p>
                    </div>
                  </Link>
                  {!c.is_self && (
                    <button
                      data-testid={`top-creator-follow-${c.username}`}
                      onClick={() => toggleFollow(c)}
                      disabled={!!followBusy[c.user_id]}
                      className={`shrink-0 flex items-center gap-1.5 px-3 py-2 font-mono text-[10px] uppercase tracking-widest transition-all disabled:opacity-40 ${
                        c.is_following
                          ? "border border-white/20 text-white/60 hover:text-white hover:border-white/40"
                          : "border border-[#39FF14]/60 text-[#39FF14] hover:bg-[#39FF14]/10"
                      }`}
                    >
                      {c.is_following ? (
                        <>
                          <UserCheck className="h-3 w-3" /> Following
                        </>
                      ) : (
                        <>
                          <UserPlus className="h-3 w-3" /> Follow
                        </>
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
            {topCreators.length > 10 && (
              <button
                data-testid="top-creators-toggle"
                onClick={() => setExpanded((s) => ({ ...s, creators: !s.creators }))}
                className="mt-3 w-full flex items-center justify-center gap-2 py-3 border border-white/10 hover:border-[#FF007F]/50 hover:text-[#FF007F] font-mono text-[10px] uppercase tracking-widest text-white/50 transition-all"
              >
                {expanded.creators ? "Show less" : `See more (+${topCreators.length - 10})`}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded.creators ? "rotate-180" : ""}`} />
              </button>
            )}
          </div>
        )}

        {/* ---- Top Sharers ---- persons who shared the most videos.
             Counts share-button taps; top 3 get pulsing badges everywhere
             they appear (video cards + profile). */}
        {!q && topSharers.length > 0 && (
          <div className="mb-10" data-testid="top-sharers-section">
            <div className="flex items-center justify-between mb-4 gap-3">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00FFFF]/70 flex items-center gap-2">
                <Share2 className="h-3 w-3" /> // top.sharers
              </p>
              <div className="flex border border-white/10">
                {["week", "all"].map((r) => (
                  <button
                    key={r}
                    data-testid={`top-sharers-range-${r}`}
                    onClick={() => setSharerRange(r)}
                    className={`px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest transition-colors ${
                      sharerRange === r
                        ? "bg-[#00FFFF]/15 text-[#00FFFF]"
                        : "text-white/40 hover:text-white/70"
                    }`}
                  >
                    {r === "week" ? "7d" : "all-time"}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              {(expanded.sharers ? topSharers : topSharers.slice(0, 10)).map((s, idx) => (
                <div
                  key={s.user_id}
                  data-testid={`top-sharer-${s.username}`}
                  className={`group flex items-center gap-4 p-3 border transition-all ${
                    idx < 3
                      ? "border-[#00FFFF]/40 bg-[#00FFFF]/5 hover:bg-[#00FFFF]/10"
                      : "border-white/10 hover:border-[#00FFFF]/50 hover:bg-[#00FFFF]/5"
                  }`}
                >
                  <span className={`font-mono text-xs w-6 tabular-nums ${
                    idx === 0 ? "text-[#FF007F] font-bold" :
                    idx === 1 ? "text-[#FF007F]" :
                    idx === 2 ? "text-[#00FFFF]" : "text-white/40"
                  }`}>
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <Link
                    to={user?.is_admin ? `/admin/user/${s.username}/shares` : `/u/${s.username}`}
                    data-testid={`top-sharer-link-${s.username}`}
                    className="flex items-center gap-3 flex-1 min-w-0"
                  >
                    {s.picture ? (
                      <img src={picUrl(s.picture)} alt="" className="h-11 w-11 object-cover border border-white/10" />
                    ) : (
                      <div className="h-11 w-11 bg-[#00FFFF]/20 flex items-center justify-center font-bold text-[#00FFFF] border border-white/10">
                        {s.name?.[0]?.toUpperCase() || "?"}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold truncate flex items-center gap-1.5">
                        {s.name || `@${s.username}`}
                        {user?.is_admin && (
                          <span className="font-mono text-[8px] uppercase tracking-widest text-[#00FFFF] border border-[#00FFFF]/50 px-1 py-0.5">
                            admin
                          </span>
                        )}
                      </p>
                      <p className="font-mono text-[11px] text-white/50 truncate">
                        <span className="text-[#00FFFF] font-bold">{formatCount(s.shares)}</span> shares
                        <span className="text-white/25"> · </span>
                        {formatCount(s.distinct_video_count)} videos
                      </p>
                    </div>
                  </Link>
                  {idx < 3 && (
                    <BadgeWithCelebration
                      category="sharer"
                      rank={idx + 1}
                      username={s.username}
                      size="sm"
                      mode="static"
                    />
                  )}
                </div>
              ))}
            </div>
            {topSharers.length > 10 && (
              <button
                data-testid="top-sharers-toggle"
                onClick={() => setExpanded((s) => ({ ...s, sharers: !s.sharers }))}
                className="mt-3 w-full flex items-center justify-center gap-2 py-3 border border-white/10 hover:border-[#00FFFF]/50 hover:text-[#00FFFF] font-mono text-[10px] uppercase tracking-widest text-white/50 transition-all"
              >
                {expanded.sharers ? "Show less" : `See more (+${topSharers.length - 10})`}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded.sharers ? "rotate-180" : ""}`} />
              </button>
            )}
          </div>
        )}

        {/* ---- Top Referrers ---- persons who got the most signups. */}
        {!q && topReferrers.length > 0 && (
          <div className="mb-10" data-testid="top-referrers-section">
            <div className="flex items-center justify-between mb-4 gap-3">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFC300]/80 flex items-center gap-2">
                <Users className="h-3 w-3" /> // top.referrers
              </p>
              <div className="flex border border-white/10">
                {["week", "all"].map((r) => (
                  <button
                    key={r}
                    data-testid={`top-referrers-range-${r}`}
                    onClick={() => setReferrerRange(r)}
                    className={`px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest transition-colors ${
                      referrerRange === r
                        ? "bg-[#FFC300]/15 text-[#FFC300]"
                        : "text-white/40 hover:text-white/70"
                    }`}
                  >
                    {r === "week" ? "7d" : "all-time"}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              {(expanded.referrers ? topReferrers : topReferrers.slice(0, 10)).map((s, idx) => (
                <div
                  key={s.user_id}
                  data-testid={`top-referrer-${s.username}`}
                  className={`group flex items-center gap-4 p-3 border transition-all ${
                    idx < 3
                      ? "border-[#FFC300]/40 bg-[#FFC300]/5 hover:bg-[#FFC300]/10"
                      : "border-white/10 hover:border-[#FFC300]/50 hover:bg-[#FFC300]/5"
                  }`}
                >
                  <span className={`font-mono text-xs w-6 tabular-nums ${
                    idx === 0 ? "text-[#FFC300] font-bold" :
                    idx === 1 ? "text-[#FFC300]" :
                    idx === 2 ? "text-[#39FF14]" : "text-white/40"
                  }`}>
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <Link
                    to={user?.is_admin ? `/admin/user/${s.username}/signups` : `/u/${s.username}`}
                    data-testid={`top-referrer-link-${s.username}`}
                    className="flex items-center gap-3 flex-1 min-w-0"
                  >
                    {s.picture ? (
                      <img src={picUrl(s.picture)} alt="" className="h-11 w-11 object-cover border border-white/10" />
                    ) : (
                      <div className="h-11 w-11 bg-[#FFC300]/20 flex items-center justify-center font-bold text-[#FFC300] border border-white/10">
                        {s.name?.[0]?.toUpperCase() || "?"}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold truncate flex items-center gap-1.5">
                        {s.name || `@${s.username}`}
                        {user?.is_admin && (
                          <span className="font-mono text-[8px] uppercase tracking-widest text-[#FFC300] border border-[#FFC300]/50 px-1 py-0.5">
                            admin
                          </span>
                        )}
                      </p>
                      <p className="font-mono text-[11px] text-white/50 truncate">
                        <span className="text-[#FFC300] font-bold">{formatCount(s.signups)}</span>{" "}
                        {s.signups === 1 ? "signup" : "signups"}
                      </p>
                    </div>
                  </Link>
                  {idx < 3 && (
                    <BadgeWithCelebration
                      category="referrer"
                      rank={idx + 1}
                      username={s.username}
                      size="sm"
                      mode="static"
                    />
                  )}
                </div>
              ))}
            </div>
            {topReferrers.length > 10 && (
              <button
                data-testid="top-referrers-toggle"
                onClick={() => setExpanded((s) => ({ ...s, referrers: !s.referrers }))}
                className="mt-3 w-full flex items-center justify-center gap-2 py-3 border border-white/10 hover:border-[#FFC300]/50 hover:text-[#FFC300] font-mono text-[10px] uppercase tracking-widest text-white/50 transition-all"
              >
                {expanded.referrers ? "Show less" : `See more (+${topReferrers.length - 10})`}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded.referrers ? "rotate-180" : ""}`} />
              </button>
            )}
          </div>
        )}

        {!q && trending.length > 0 && (
          <div data-testid="trending-section">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40 mb-4">
              // trending.tags
            </p>
            <div className="flex flex-wrap gap-2">
              {trending.map((t) => (
                <button
                  key={t.tag}
                  onClick={() => setQ(`#${t.tag}`)}
                  data-testid={`trending-${t.tag}`}
                  className="border border-white/10 hover:border-[#39FF14]/60 hover:text-[#39FF14] px-4 py-2 font-mono text-xs uppercase tracking-widest transition-all"
                >
                  #{t.tag} <span className="text-white/30 ml-1">{t.count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {q && (
          <>
            {results.users.length > 0 && (
              <div className="mb-10">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40 mb-4">
                  // users
                </p>
                <div className="space-y-2">
                  {results.users.map((u) => (
                    <Link
                      key={u.user_id}
                      to={user?.is_admin ? `/admin/user/${u.username}/shares` : `/u/${u.username}`}
                      data-testid={`search-user-${u.username}`}
                      className="flex items-center gap-4 p-3 border border-white/10 hover:border-[#00FFFF]/50 hover:bg-[#00FFFF]/5 transition-all"
                    >
                      {u.picture ? (
                        <img src={picUrl(u.picture)} alt="" className="h-10 w-10 object-cover" />
                      ) : (
                        <div className="h-10 w-10 bg-[#FF007F]/20 flex items-center justify-center font-bold text-[#FF007F]">
                          {u.name?.[0]?.toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold flex items-center gap-1.5">
                          {u.name}
                          {user?.is_admin && (
                            <span className="font-mono text-[8px] uppercase tracking-widest text-[#00FFFF] border border-[#00FFFF]/50 px-1 py-0.5">
                              admin
                            </span>
                          )}
                        </p>
                        <p className="font-mono text-xs text-white/40">@{u.username}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {results.videos.length > 0 && (
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40 mb-4">
                  // videos
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {results.videos.map((v) => (
                    <Link
                      key={v.id}
                      to={`/v/${v.id}?src=discover`}
                      data-testid={`search-video-${v.id}`}
                      className="aspect-[9/16] bg-white/5 relative group overflow-hidden"
                    >
                      <video
                        src={streamUrl(v.id)}
                        className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                        muted
                        playsInline
                        preload="metadata"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                      <div className="absolute bottom-2 left-2 right-2 text-white text-xs">
                        <p className="truncate">@{v.author?.username}</p>
                        <p className="font-mono text-[10px] text-white/60">
                          ♥ {v.like_count}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {results.users.length === 0 && results.videos.length === 0 && (
              <div
                data-testid="search-empty"
                className="text-center py-16 text-white/40"
              >
                <p className="font-display text-lg">No matches</p>
                <p className="font-mono text-xs mt-2 uppercase tracking-widest">
                  the void echoes back
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
